<template>
  <div class="dark-theme-wrapper">
    <div class="container py-4">
      <h2 class="section-title text-center mb-4">
        STATISTIKY KOLA <span class="text-red">{{ roundNumber }}</span>
      </h2>

      <div class="filters-card mx-auto mb-4">
        <div class="row g-3 align-items-end">
          <div class="col-6">
            <label class="form-label">KOLO</label>
            <select
              v-model="roundNumber"
              @change="fetchData"
              class="form-select dark-input"
            >
              <option
                v-for="round in availableRounds"
                :key="round"
                :value="round"
              >
                {{ round }}.
              </option>
            </select>
          </div>
          <div class="col-6">
            <label class="form-label">HRÁČ</label>
            <select
              v-model="selectedUser"
              @change="updateView"
              class="form-select dark-input"
            >
              <option
                v-for="user in availableUsers"
                :key="user.id"
                :value="user.id"
              >
                {{ user.username }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div v-if="loading" class="text-center text-muted my-5">
        <div class="spinner-border text-danger mb-2" role="status"></div>
        <p>Počítám statistiky...</p>
      </div>

      <div v-else>
        <div class="chart-section mb-5 fade-in" v-if="chartData">
          <h3 class="chart-title text-center mb-3">VÝVOJ BODŮ V ČASE</h3>
          <div class="chart-container">
            <Line :data="chartData" :options="chartOptions" />
          </div>
          <p class="text-center text-muted small mt-2">
            Červená: {{ getSelectedUserName() }} | Šedá: Průměr všech hráčů
          </p>
        </div>

        <div v-if="matches.length" class="summary-card mb-5 fade-in">
          <div class="d-flex justify-content-between align-items-center">
            <div class="text-center w-100">
              <span class="summary-label">ZÁPASY</span>
              <span class="summary-value">{{ stats.matchPoints }}</span>
            </div>
            <div class="plus-sign">+</div>
            <div class="text-center w-100">
              <span class="summary-label">OTÁZKA</span>
              <span class="summary-value">{{ stats.questionPoints }}</span>
            </div>
            <div class="plus-sign" v-if="userTip?.useJoker">×</div>
            <div class="text-center w-100" v-if="userTip?.useJoker">
              <span class="summary-label text-joker">JOKER</span>
              <span class="summary-value text-joker">2</span>
            </div>
            <div class="equals-sign">=</div>
            <div class="text-center w-100">
              <span class="summary-label text-total">CELKEM</span>
              <span class="summary-value text-total">{{
                stats.totalPoints
              }}</span>
            </div>
          </div>
        </div>

        <div v-if="matches.length" class="matches-grid">
          <div
            v-for="match in matches"
            :key="match.id"
            class="stats-card"
            :class="{
              'perfect-hit':
                getPointsForMatch(match, getUserTipForMatch(match.id)) === 10,
            }"
          >
            <div class="match-header">
              <div class="team-col">
                <img :src="match.homeLogo" alt="Home" class="team-logo" />
                <span class="team-name">{{ match.homeTeam }}</span>
              </div>
              <div class="score-board">
                <span class="final-score"
                  >{{ match.finalHomeScore }} : {{ match.finalAwayScore }}</span
                >
                <span class="match-status">KONEC</span>
              </div>
              <div class="team-col">
                <img :src="match.awayLogo" alt="Away" class="team-logo" />
                <span class="team-name">{{ match.awayTeam }}</span>
              </div>
            </div>

            <hr class="separator" />

            <div class="user-prediction">
              <div v-if="getUserTipForMatch(match.id)" class="tip-details">
                <div class="d-flex justify-content-between align-items-center">
                  <div class="text-start">
                    <span class="label d-block">TIP:</span>
                    <span class="value font-bold">
                      {{ getUserTipForMatch(match.id).homeScore }} :
                      {{ getUserTipForMatch(match.id).awayScore }}
                    </span>
                  </div>

                  <div class="text-end">
                    <span class="label d-block">BODY:</span>
                    <span
                      :class="[
                        'points-badge',
                        getPointsColor(
                          getPointsForMatch(match, getUserTipForMatch(match.id))
                        ),
                      ]"
                    >
                      +{{
                        getPointsForMatch(match, getUserTipForMatch(match.id))
                      }}
                    </span>
                  </div>
                </div>
              </div>
              <div v-else class="no-tip text-center">
                <span class="text-muted small">❌ Bez tipu</span>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="placeholder-section">
          <p>
            Pro toto kolo zatím nejsou k dispozici žádné vyhodnocené zápasy.
          </p>
        </div>

        <div v-if="questionData" class="stats-card mt-5 bonus-card fade-in">
          <h3 class="bonus-title">🔥 DOPLŇUJÍCÍ OTÁZKA</h3>
          <p class="question-text">{{ questionData.question }}</p>

          <div class="bonus-result mt-3">
            <div
              v-if="userTip && userTip.questionAnswer"
              class="d-flex justify-content-between align-items-center"
            >
              <div>
                <span
                  class="text-muted d-block text-start"
                  style="font-size: 0.8rem"
                  >TVOJE ODPOVĚĎ:</span
                >
                <span class="font-bold text-white" style="font-size: 1.1rem">{{
                  userTip.questionAnswer
                }}</span>
              </div>

              <div v-if="questionData.correctAnswer" class="text-center">
                <span class="text-muted d-block" style="font-size: 0.8rem"
                  >SPRÁVNĚ:</span
                >
                <span class="text-success font-bold">{{
                  questionData.correctAnswer
                }}</span>
              </div>

              <div class="text-end">
                <span class="text-muted d-block" style="font-size: 0.8rem"
                  >BODY:</span
                >
                <span
                  :class="['points-badge', getPointsColor(getQuestionPoints())]"
                >
                  +{{ getQuestionPoints() }}
                </span>
              </div>
            </div>
            <div v-else class="text-center text-muted">Bez odpovědi.</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { db } from "../auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";

// --- CHART.JS IMPORTY ---
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "vue-chartjs";

// Registrace komponent grafu
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default {
  name: "TipStatistics",
  components: {
    Line, // Registrace komponenty Line
  },
  data() {
    return {
      roundNumber: 1,
      matches: [],
      userTip: null,
      questionData: null,
      availableRounds: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15], // Můžete generovat dynamicky
      availableUsers: [],
      selectedUser: null,
      loading: true,

      // Data pro graf
      chartData: null,
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            grid: { color: "#333" }, // Tmavá mřížka
            ticks: { color: "#888" },
          },
          x: {
            grid: { display: false },
            ticks: { color: "#888" },
          },
        },
        plugins: {
          legend: {
            labels: { color: "#fff" }, // Bílá legenda
          },
          tooltip: {
            mode: "index",
            intersect: false,
          },
        },
      },
    };
  },
  computed: {
    stats() {
      let matchPoints = 0;
      this.matches.forEach((match) => {
        const tip = this.getUserTipForMatch(match.id);
        matchPoints += this.getPointsForMatch(match, tip);
      });

      const questionPoints = this.getQuestionPoints();
      let total = matchPoints + questionPoints;

      if (this.userTip && this.userTip.useJoker) {
        total *= 2;
      }

      return {
        matchPoints,
        questionPoints,
        totalPoints: total,
      };
    },
  },
  async created() {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    // Nejprve načteme všechny uživatele (potřebujeme je pro graf i dropdown)
    await this.fetchUsers();

    if (currentUser) {
      this.selectedUser = currentUser.uid;
    } else if (this.availableUsers.length > 0) {
      this.selectedUser = this.availableUsers[0].id;
    }

    await this.fetchCurrentRound();
    this.fetchData();
  },
  methods: {
    // --- GRAF LOGIKA ---
    prepareChartData() {
      if (!this.availableUsers.length) return;

      // 1. Zjistíme maximální počet kol, která se hrála (podle dat uživatelů)
      let maxRound = 0;
      this.availableUsers.forEach((u) => {
        if (u.rounds) {
          const rounds = Object.keys(u.rounds).map(Number);
          if (rounds.length) maxRound = Math.max(maxRound, Math.max(...rounds));
        }
      });

      if (maxRound === 0) return; // Žádná data

      const labels = Array.from(
        { length: maxRound },
        (_, i) => `Kolo ${i + 1}`
      );

      // 2. Data vybraného uživatele (Kumulativní)
      const userDataPoints = [];
      const user = this.availableUsers.find((u) => u.id === this.selectedUser);
      let userCumulative = 0;

      for (let r = 1; r <= maxRound; r++) {
        const points =
          user && user.rounds && user.rounds[r] ? user.rounds[r] : 0;
        userCumulative += points;
        userDataPoints.push(userCumulative);
      }

      // 3. Data průměru (Kumulativní)
      const avgDataPoints = [];
      let avgCumulative = 0;

      for (let r = 1; r <= maxRound; r++) {
        let sum = 0;
        let count = 0;

        this.availableUsers.forEach((u) => {
          if (u.rounds && u.rounds[r] !== undefined) {
            sum += u.rounds[r];
            count++;
          }
        });

        const roundAvg = count > 0 ? sum / count : 0;
        avgCumulative += roundAvg;
        avgDataPoints.push(avgCumulative);
      }

      // 4. Sestavení objektu pro Chart.js
      this.chartData = {
        labels: labels,
        datasets: [
          {
            label: user ? user.username : "Hráč",
            borderColor: "#dc3545", // Červená
            backgroundColor: "rgba(220, 53, 69, 0.1)",
            data: userDataPoints,
            tension: 0.3, // Zaoblení křivky
            pointRadius: 4,
            pointBackgroundColor: "#dc3545",
            fill: true,
          },
          {
            label: "Průměr soutěže",
            borderColor: "#6c757d", // Šedá
            backgroundColor: "transparent",
            borderDash: [5, 5], // Přerušovaná čára
            data: avgDataPoints,
            tension: 0.3,
            pointRadius: 0, // Skryté body pro čistší vzhled
          },
        ],
      };
    },

    // Pokud změníme uživatele v dropdownu, jen překreslíme graf a načteme tipy
    updateView() {
      this.fetchUserTip();
      this.prepareChartData();
    },

    async fetchCurrentRound() {
      try {
        const roundSnap = await getDoc(doc(db, "settings", "visibleRound"));
        if (roundSnap.exists()) {
          this.roundNumber = roundSnap.data().roundNumber;
        }
      } catch (error) {
        console.error(error);
      }
    },
    async fetchUsers() {
      try {
        const usersSnapshot = await getDocs(collection(db, "users"));
        // Ukládáme si i 'rounds' data pro graf
        this.availableUsers = usersSnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((user) => user.role !== "admin");
      } catch (error) {
        console.error(error);
      }
    },
    async fetchData() {
      this.loading = true;
      this.matches = [];
      this.userTip = null;

      // Načteme data pro zobrazení kola
      await Promise.all([
        this.fetchMatches(),
        this.fetchUserTip(),
        this.fetchQuestionData(),
      ]);

      // Připravíme data pro graf (na základě availableUsers, které už máme)
      this.prepareChartData();

      this.loading = false;
    },
    async fetchMatches() {
      try {
        const q = query(
          collection(db, "matches"),
          where("roundNumber", "==", this.roundNumber)
        );
        const snap = await getDocs(q);
        this.matches = snap.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter(
            (m) => m.finalHomeScore !== undefined && m.finalHomeScore !== null
          );
      } catch (error) {
        console.error(error);
      }
    },
    async fetchUserTip() {
      if (!this.selectedUser) return;
      try {
        const q = query(
          collection(db, "tips"),
          where("roundNumber", "==", this.roundNumber),
          where("user", "==", this.selectedUser)
        );
        const snap = await getDocs(q);
        this.userTip = !snap.empty ? snap.docs[0].data() : null;
      } catch (error) {
        console.error(error);
      }
    },
    async fetchQuestionData() {
      try {
        const docSnap = await getDoc(
          doc(db, "questions", String(this.roundNumber))
        );
        this.questionData = docSnap.exists() ? docSnap.data() : null;
      } catch (error) {
        console.error(error);
      }
    },
    getUserTipForMatch(matchId) {
      if (this.userTip && this.userTip.tips) {
        return this.userTip.tips[matchId];
      }
      return null;
    },
    getPointsForMatch(match, tip) {
      if (!tip) return 0;
      const mHome = Number(match.finalHomeScore);
      const mAway = Number(match.finalAwayScore);
      const tHome = Number(tip.homeScore);
      const tAway = Number(tip.awayScore);

      // 1. Přesný výsledek = 10 bodů
      if (tHome === mHome && tAway === mAway) return 10;

      const matchDiff = Math.sign(mHome - mAway);
      const tipDiff = Math.sign(tHome - tAway);

      // 2. Uhodnutá tendence (výhra domácích / výhra hostů / remíza)
      if (matchDiff === tipDiff) {
        // NOVÉ: Uhodnutá remíza (matchDiff === 0), ale jiným skóre (např. tip 1:1, konec 2:2)
        if (matchDiff === 0) {
          return 7;
        }

        // Uhodnutý vítěz + trefený přesný počet gólů jednoho z týmů
        if (tHome === mHome || tAway === mAway) {
          return 7;
        }

        // Pouze uhodnutý vítěz (např. tip 2:0, konec 3:1)
        return 3;
      }

      // 3. Zcela špatný tip
      return 0;
    },
    getQuestionPoints() {
      if (this.userTip?.questionAnswer && this.questionData?.correctAnswer) {
        return this.userTip.questionAnswer.toLowerCase() ===
          this.questionData.correctAnswer.toLowerCase()
          ? 5
          : 0;
      }
      return 0;
    },
    getPointsColor(points) {
      if (points === 10) return "badge-perfect";
      if (points === 7) return "badge-great";
      if (points === 5) return "badge-good";
      if (points === 3) return "badge-ok";
      return "badge-zero";
    },
    getSelectedUserName() {
      const u = this.availableUsers.find((u) => u.id === this.selectedUser);
      return u ? u.username : "Hráč";
    },
  },
};
</script>

<style scoped>
/* Stejné styly jako minule + styly pro graf */
.dark-theme-wrapper {
  background-color: #000000;
  color: #ffffff;
  font-family: "Roboto", sans-serif;
}
.section-title {
  font-size: 1.8rem;
  font-weight: 700;
  letter-spacing: 1px;
  color: #fff;
}
.text-red {
  color: #dc3545;
}

/* Filtry */
.filters-card {
  background: #111;
  border: 1px solid #333;
  padding: 20px;
  border-radius: 12px;
  max-width: 600px;
}
.form-label {
  font-size: 0.75rem;
  font-weight: bold;
  color: #888;
  letter-spacing: 1px;
  margin-bottom: 5px;
  display: block;
}
.dark-input {
  background-color: #000;
  border: 1px solid #444;
  color: #fff;
  padding: 10px;
  border-radius: 6px;
  width: 100%;
}
.dark-input:focus {
  border-color: #dc3545;
  outline: none;
}

/* GRAF */
.chart-section {
  background: #111;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
}
.chart-title {
  font-size: 1rem;
  font-weight: bold;
  color: #888;
  letter-spacing: 1px;
}
.chart-container {
  position: relative;
  height: 300px; /* Pevná výška grafu */
  width: 100%;
}

/* Souhrnná karta */
.summary-card {
  background: linear-gradient(135deg, #1a1a1a 0%, #000 100%);
  border: 1px solid #333;
  border-radius: 12px;
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}
.summary-label {
  font-size: 0.7rem;
  color: #888;
  display: block;
  letter-spacing: 1px;
  margin-bottom: 5px;
}
.summary-value {
  font-size: 1.5rem;
  font-weight: 900;
  color: #fff;
}
.plus-sign,
.equals-sign {
  font-size: 1.5rem;
  color: #444;
  font-weight: bold;
}
.text-total {
  color: #dc3545;
}
.text-joker {
  color: #ffc107;
}

/* Grid */
.matches-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

/* Karta */
.stats-card {
  background: #111;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 20px;
  transition: transform 0.2s;
}
.stats-card:hover {
  border-color: #555;
  transform: translateY(-2px);
}
.perfect-hit {
  border-color: #28a745;
  box-shadow: 0 0 15px rgba(40, 167, 69, 0.2);
}

.match-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}
.team-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 30%;
}
.team-logo {
  width: 45px;
  height: 45px;
  object-fit: contain;
  margin-bottom: 5px;
}
.team-name {
  font-size: 0.8rem;
  font-weight: bold;
  text-align: center;
  color: #ddd;
}
.score-board {
  text-align: center;
  width: 40%;
}
.final-score {
  font-size: 1.8rem;
  font-weight: 900;
  color: #fff;
  letter-spacing: 2px;
}
.match-status {
  font-size: 0.6rem;
  color: #dc3545;
  font-weight: bold;
  text-transform: uppercase;
}
.separator {
  border-top: 1px solid #222;
  margin: 10px 0 15px 0;
  opacity: 1;
}

.user-prediction {
  background: #000;
  border-radius: 8px;
  padding: 10px 15px;
}
.label {
  font-size: 0.8rem;
  color: #888;
}
.value {
  color: #fff;
  font-size: 1rem;
}
.font-bold {
  font-weight: bold;
}

.points-badge {
  padding: 4px 10px;
  border-radius: 4px;
  font-weight: bold;
  font-size: 0.9rem;
  color: #000;
}
.badge-perfect {
  background-color: #28a745;
  color: white;
}
.badge-great {
  background-color: #cddc39;
}
.badge-good {
  background-color: #17a2b8;
  color: white;
}
.badge-ok {
  background-color: #ffc107;
}
.badge-zero {
  background-color: #333;
  color: #888;
}

.bonus-card {
  border-color: #dc3545;
  background: linear-gradient(180deg, #111 0%, #0d0d0d 100%);
}
.bonus-title {
  font-size: 1rem;
  font-weight: bold;
  color: #dc3545;
  text-transform: uppercase;
  margin-bottom: 10px;
}
.question-text {
  font-size: 1.1rem;
  font-style: italic;
  color: #fff;
  margin-bottom: 0;
}
.bonus-result {
  background: #000;
  padding: 15px;
  border-radius: 8px;
  margin-top: 15px;
}
.placeholder-section {
  text-align: center;
  color: #666;
  padding: 40px;
  border: 1px dashed #333;
  border-radius: 12px;
}
.fade-in {
  animation: fadeIn 0.5s ease-out;
}
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
